import React from 'react';
import contentConstants from 'constants/content';
import autoReplenishmentConstants from 'constants/AutoReplenishment';
import { wrapFunctionalComponent } from 'utils/framework';
import { colors, space } from 'style/config';
import { Flex, Text, Link } from 'components/ui';
import ComponentList from 'components/Content/ComponentList';
import PleaseSignInBlock from 'components/RichProfile/MyAccount/PleaseSignIn';
import AutoReplenishment from 'components/RichProfile/MyAccount/AutoReplenishment';
import AutoReplenishmentSummary from 'components/RichProfile/MyAccount/AutoReplenishment/AutoReplenishmentSummary';
import AutoReplenishmentSummarySkeleton from 'components/RichProfile/MyAccount/AutoReplenishment/AutoReplenishmentSummary/AutoReplenishmentSummarySkeleton';
import { useAutoReplenishmentHub } from 'components/RichProfile/MyAccount/AutoReplenishment/AutoReplenishmentHub/hooks/useAutoReplenishmentHub';
import { useSubscriptions } from 'components/RichProfile/MyAccount/AutoReplenishment/AutoReplenishmentHub/hooks/useSubscriptions';
import { useModals } from 'components/RichProfile/MyAccount/AutoReplenishment/Modals/hooks/useModals';

const { CONTEXTS } = contentConstants;
const { CARD_SIZE_CONFIG } = autoReplenishmentConstants;

/**
 * Returns true if the given subscription counts warrant showing the summary panel.
 * Canceled subscriptions only count when the empty hub feature flag is enabled.
 */
const checkHasSummary = ({ active, paused, cancelled }, isAutoReplenishEmptyHubEnabled) =>
    active > 0 || paused > 0 || (isAutoReplenishEmptyHubEnabled && cancelled > 0);

/**
 * Entry point for the Auto-Replenishment hub page.
 */
const AutoReplenishmentHub = () => {
    const { openFAQModal } = useModals();
    const {
        allSubscriptions,
        currentSubscription,
        currentSubscriptionPaymentData,
        isSubscriptionsLoaded,
        profileSubCounts,
        setCurrentSubscription,
        updateCurrentSubscription
    } = useSubscriptions();
    const {
        localization, featureFlags, cmsData, biPoints, isGuest, user, isLoggedIn
    } = useAutoReplenishmentHub();

    const { isAutoReplenishEmptyHubEnabled } = featureFlags;

    const hasSummaryFromProfile = checkHasSummary(profileSubCounts, isAutoReplenishEmptyHubEnabled);
    const hasSummary = checkHasSummary(allSubscriptions, isAutoReplenishEmptyHubEnabled);

    return (
        <>
            {isLoggedIn && !isSubscriptionsLoaded && hasSummaryFromProfile && <AutoReplenishmentSummarySkeleton />}
            {isLoggedIn && isSubscriptionsLoaded && hasSummary && (
                <AutoReplenishmentSummary
                    allSubscriptions={allSubscriptions}
                    biPoints={biPoints}
                    isAutoReplenishEmptyHubEnabled={isAutoReplenishEmptyHubEnabled}
                    localization={localization}
                />
            )}

            <AutoReplenishment
                allSubscriptions={allSubscriptions}
                currentSubscription={currentSubscription}
                currentSubscriptionPaymentData={currentSubscriptionPaymentData}
                featureFlags={featureFlags}
                isLoggedIn={isLoggedIn}
                localization={localization}
                setCurrentSubscription={setCurrentSubscription}
                updateCurrentSubscription={updateCurrentSubscription}
                user={user}
            />
            {isLoggedIn && isSubscriptionsLoaded && (
                <>
                    {isAutoReplenishEmptyHubEnabled && cmsData?.bottomContent && (
                        <ComponentList
                            context={CONTEXTS.CONTAINER}
                            customCardSize={CARD_SIZE_CONFIG}
                            items={cmsData.bottomContent}
                        />
                    )}
                    <Flex css={styles.buttonContainer}>
                        <Text
                            children={`${localization.needHelp} `}
                            is='p'
                        />
                        <Link
                            children={localization.viewFAQ}
                            css={styles.link}
                            onClick={openFAQModal}
                        />
                    </Flex>
                </>
            )}
            {isGuest && <PleaseSignInBlock />}
        </>
    );
};

const styles = {
    buttonContainer: {
        marginTop: space[2],
        justifyContent: 'center'
    },
    link: {
        color: colors.blue
    }
};

export default wrapFunctionalComponent(AutoReplenishmentHub, 'AutoReplenishmentHub');
